import React, { useState, useEffect } from "react";
import { Menu, X, LogOut } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { splitLangPath, withLang } from "../i18n/urls";
import { COMPANY, EXPLORE_MENU } from "../data/mock";
import LanguageSwitcher from "./LanguageSwitcher";
import { getSessionToken, getSessionUser, clearSession } from "../lib/sessionApi";

const Header = ({ onAccess }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = splitLangPath(pathname).lang;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(getSessionUser());
  const isLogged = !!getSessionToken();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    // Reagisci a cambi di sessione (login/logout) anche in altre tab
    const onStorage = () => setUser(getSessionUser());
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const handleLogout = () => {
    clearSession();
    setUser(null);
    navigate("/");
    window.location.reload();
  };

  const dashboardPath = (() => {
    const role = (user?.role || "").toLowerCase();
    if (role === "admin") return "/admin";
    if (role === "staff" || role === "collaborator") return "/arabell-staff";
    return "/arabell-member";
  })();

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-black/70 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <button
            onClick={() => setOpen(true)}
            data-testid="menu-toggle"
            className="text-[11px] tracking-[0.4em] uppercase text-white/90 hover:text-arabell-gold transition-colors duration-300 flex items-center gap-3"
            aria-label="Open menu"
          >
            <Menu className="w-4 h-4" strokeWidth={1.2} />
            <span className="hidden sm:inline">{t("nav.home", "Explore")}</span>
          </button>

          <a href={withLang("/", lang)} className="flex items-center">
            <img
              src={COMPANY.logoUrl}
              alt="Arabell Group"
              className="h-10 md:h-12 w-auto"
              style={{ filter: "invert(1) brightness(1.1)" }}
            />
          </a>

          <div className="flex items-center gap-4 md:gap-5">
            <LanguageSwitcher />
            {isLogged ? (
              <div className="flex items-center gap-2">
                <Link
                  to={dashboardPath}
                  data-testid="header-dashboard-btn"
                  className="hidden sm:inline-flex items-center gap-2 px-5 md:px-7 py-2 rounded-full bg-arabell-gold text-black text-[11px] tracking-[0.3em] uppercase hover:bg-white transition-colors duration-300"
                  title={user?.username ? `Connesso come ${user.username}` : "Vai alla dashboard"}
                >
                  {user?.role === "admin" ? "ADMIN" : (user?.role === "staff" || user?.role === "collaborator" ? "STAFF" : "AREA")}
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="header-logout-btn"
                  className="px-4 py-2 rounded-full border border-white/30 text-white text-[11px] tracking-[0.3em] uppercase hover:border-arabell-gold hover:text-arabell-gold transition-colors inline-flex items-center gap-2"
                  title="Esci"
                >
                  <LogOut className="w-3.5 h-3.5" strokeWidth={1.4} />
                  <span className="hidden md:inline">Esci</span>
                </button>
              </div>
            ) : (
              <button
                onClick={onAccess}
                data-testid="header-access-btn"
                className="px-5 md:px-7 py-2 rounded-full bg-white text-black text-[11px] tracking-[0.3em] uppercase hover:bg-arabell-gold transition-colors duration-300"
              >
                {t("nav.login")}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Explore Drawer */}
      <div
        className={`fixed inset-0 z-[100] transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-black/85 backdrop-blur-md"
          onClick={() => setOpen(false)}
        />
        <aside
          className={`absolute top-0 left-0 bottom-0 w-full sm:w-[420px] bg-[#0a0a0a] border-r border-white/10 transition-transform duration-500 flex flex-col ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="h-[72px] flex items-center justify-between px-8 border-b border-white/10 shrink-0">
            <span className="text-[11px] tracking-[0.4em] uppercase text-white/80">
              {t("drawer.menu")}
            </span>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-arabell-gold"
              aria-label={t("drawer.close")}
            >
              <X className="w-5 h-5" strokeWidth={1.2} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <nav className="px-8 py-10 flex flex-col gap-2">
              {EXPLORE_MENU.map((m, i) => (
                <Link
                  key={m.label}
                  to={withLang(m.href, lang)}
                  data-testid={`drawer-link-${m.href}`}
                  onClick={() => setOpen(false)}
                  className="group block py-3 border-b border-white/5"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <span className="text-[10px] tracking-[0.4em] text-white/30 mr-4">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl text-white/90 group-hover:text-arabell-gold tracking-wide transition-colors duration-300">
                    {t(`drawer_items.${m.href}`, { defaultValue: m.label })}
                  </span>
                </Link>
              ))}
            </nav>
            <div className="px-8 mt-2 mb-8">
              <p className="text-[10px] tracking-[0.3em] uppercase text-white/30 mb-2">
                {t("nav.contact")}
              </p>
              <a
                href={`tel:${COMPANY.whatsapp.replace(/\s/g, "")}`}
                className="text-white/80 hover:text-arabell-gold text-sm tracking-wider"
              >
                {COMPANY.whatsapp}
              </a>
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Header;
